import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { ArrayHelpers, Box, Copy, Help, Loading, StringHelpers, SystemText } from 'e4shared';
import CompetencyTasks from 'views/user/components/competencyTasks';
import ManageTask from 'views/user/components/manageTask';
import CompetencyOverTime from '../competencyOverTime';
import Rater from './components/rater';

import { updateRating, updateFlag } from 'actions/profile';
import { isPast } from 'helpers/dates';
import { hasModule } from 'helpers/profile';
import { ratingColour } from 'helpers/metrics';
import { apiGet } from 'services';
import DataRow from '../dataRow';
import { getPermissions } from 'helpers/permissions';
import posthog from 'posthog-js';
import PositiveIndicators from 'components/user/positiveIndicators';
import { systemTranslationTypes, translateSys } from 'helpers/translations';

const defaultRatingText = SystemText('Admin/Config/Defaults/Ratings');

export const Competencies = props => {
    const dispatch = useDispatch();

    const focusedCompetencyCount = 6;

    const [area, setArea] = React.useState(null);
    const [areas, setAreas] = React.useState([]);
    const [filtered, setFiltered] = React.useState([]);
    const [currentArea, setCurrentArea] = React.useState({});
    let [competencies, setCompetencies] = React.useState(null);
    let [loaded, setLoaded] = React.useState(false);
    let [filteredCompetencies, setFilteredCompetencies] = React.useState([]);
    let [filteredAverage, setFilteredAverage] = React.useState(0);
    const [competency, setCompetency] = React.useState(null);
    const [competencyId, setCompetencyId] = React.useState(null);
    const [showAddTask, setShowAddTask] = React.useState(false);
    const [showTasks, setShowTasks] = React.useState(false);
    const [showOverTime, setShowOverTime] = React.useState(false);
    const [overTimeCompetency, setOverTimeCompetency] = React.useState(null);
    const [filterOnFocused, setFilterOnFocused] = React.useState(true);
    const [permissions, setPermissions] = React.useState(getPermissions());

    const { coaching, downline, user } = useSelector(state => state.user);

    const text = {
        filteredCompetencies: SystemText('User121/Competencies/FilteredCompetencies'),
        focusAreas: SystemText('User121/Competencies/FocusAreas'),
        focused: SystemText('User121/Competencies/Focused'),
        clear: SystemText('User121/Competencies/ClearFilter'),
        byArea: SystemText('User121/Competencies/ByArea'),
        allMatching: SystemText('User121/Competencies/AllMatching'),
        areaForAttention: SystemText('User121/Competencies/AreaForAttention'),
        addFlag: SystemText('User121/Competencies/AddFlag'),
        removeFlag: SystemText('User121/Competencies/RemoveFlag'),
        noCompetencyFlag: SystemText('User121/Competencies/NoCompetencyFlag'),
        averageRating: SystemText('User121/Competencies/AverageRating'),
        buttons: {
            search: SystemText('User121/Competencies/Buttons/Search'),
            overtime: SystemText('User121/Competencies/Buttons/Overtime'),
            addObjective: SystemText('User121/Competencies/Buttons/AddObjective'),
            viewObjectives: SystemText('User121/Competencies/Buttons/ViewObjectives'),
        },
        none: SystemText('User121/Competencies/None'),
        title: SystemText('User121/Competencies/Title'),
        noCompetencies: {
            you: SystemText('User121/Competencies/NoCompetencies/You'),
            them: SystemText('User121/Competencies/NoCompetencies/Them', props.profile.first_Name),
        },
    };

    React.useEffect(() => {
        if (props.profile?.id && user?.id) {
            setPermissions(getPermissions(props.profile, user, coaching, downline));
        }
    }, [props.profile?.id, user?.id]);

    const updateFlagFunc = (competency_Id, flagged) => {
        const { benchmark } = props;
        const item = {
            id: competency_Id,
            flagged,
            profile_Id: props.profile.id,
        };

        dispatch(updateFlag(item, benchmark));
        const temp = [...competencies];
        const comp = temp.find(c => c.id === competency_Id);
        comp.flagged = flagged;
        setCompetencies(temp);
        //api.loadCompetencies();
    };

    const updateRatingFunc = (competency_Id, level) => {
        const { benchmark } = props;
        const rating = {
            competency_Id,
            level,
            profile_Id: props.profile.id,
        };

        const callback = () => {
            //api.loadCompetencies();
            const temp = [...competencies];
            const comp = temp.find(c => c.id === competency_Id);
            if (comp) comp.level = level;
            setCompetencies(temp);
        };

        dispatch(updateRating(rating, benchmark, callback));
    };

    const addTask = competency => {
        setShowAddTask(true);
        setCompetency(competency);
    };

    const showTasksModal = competencyId => {
        setShowTasks(true);
        setCompetencyId(competencyId);
    };

    const api = {
        loadCompetencies: () => {
            setLoaded(false);
            apiGet(`profile/${props.profile?.id}/competencies/${props.benchmark}`, user.guid, response => {
                setCompetencies(response);
                setLoaded(true);
            });
        },
    };

    const sorter = (a, b) => {
        const ax = filterOnFocused && !filtered ? `${a.flagged ? 1 : 2}-${a.level}-${a.name}` : a.name;
        const bx = filterOnFocused && !filtered ? `${b.flagged ? 1 : 2}-${b.level}-${b.name}` : b.name;
        return ax > bx ? 1 : -1;
    };

    React.useEffect(() => {
        setFiltered(props.filtered);
    }, [props.filtered]);

    React.useEffect(() => {
        api.loadCompetencies();
    }, [props.profile, props.benchmark]);

    React.useEffect(() => {
        props.setLastPaint(new Date());
    }, [filterOnFocused, filtered, area, competencies]);

    React.useEffect(() => {
        const temp = competencies?.reduce((all, c) => {
            if (!all.some(a => a.id === c.area_Id)) all.push({ id: c.area_Id, name: c.area });
            return all;
        }, []);
        setAreas(temp);
    }, [competencies]);

    React.useEffect(() => {
        if (areas) {
            const cur = area && areas.some(a => a.id === area.id) ? area : areas.length ? areas[0] : null;
            setCurrentArea(cur);
        }
    }, [area, areas]);

    React.useEffect(() => {
        if (competencies && loaded) {
            let temp = [...competencies];

            if (filtered) {
                temp = temp.filter(x => filtered.some(f => f.id === x.id)).sort(sorter);
            } else if (filterOnFocused) {
                temp = temp.sort(sorter).slice(0, focusedCompetencyCount);
            } else {
                temp = temp.filter(x => x.area_Id === currentArea?.id).sort(sorter);
            }

            setFilteredCompetencies(temp);
        }
    }, [loaded, filterOnFocused, currentArea?.id, filtered]);

    React.useEffect(() => {
        const avg = filteredCompetencies.length
            ? (filteredCompetencies.reduce((c, a) => c + a?.level || 0, 0) / filteredCompetencies.length).toFixed(1)
            : 0;

        setFilteredAverage(avg);
    }, [filteredCompetencies]);

    if (!competencies && loaded) return null;

    const isMe = user && props.profile && user.id === props.profile.id;

    const currentIndex =
        areas && currentArea?.id && areas.indexOf(areas.find(a => a.id.toString() === currentArea.id.toString()));
    const nextIndex = areas && currentIndex + 1 > areas.length - 1 ? 0 : currentIndex + 1;
    const prevIndex = areas && currentIndex === 0 ? areas.length - 1 : currentIndex - 1;

    const labelClass = rating => {
        return `label-${ratingColour(rating)}`;
    };

    const competencyCard = (c, i) => {
        if (filterOnFocused && i >= focusedCompetencyCount) return null;

        const tasks = c.tasks.length;
        const hasCRM = user.crmIntegration && c.crm_Job_Ids && c.crm_Job_Ids.length > 0;
        const pastTasks = c.tasks.filter(t => isPast(t.due) && !t.date_Completed).length;
        const flagHelp = !permissions.one2ones.canFlag
            ? c.flagged
                ? text.areaForAttention
                : ''
            : c.level
            ? c.flagged
                ? text.removeFlag
                : text.addFlag
            : text.noCompetencyFlag;

        let integrationsList = c?.integrationFields
            ? c.integrationFields.reduce(
                  (all, cur) => (all.some(x => x === cur.integration) ? all : all.concat([cur.integration])),
                  []
              )
            : [];

        integrationsList = hasCRM ? [user.crmIntegration].concat(integrationsList) : integrationsList;

        const overlaid = SystemText(
            'User121/Competencies/Overlaid',
            StringHelpers.arrayToString(integrationsList, ',', SystemText('User121/Competencies/Overlaid/And'))
        );

        const withText = integrationsList.length === 0 ? '' : overlaid;

        const prefix = (
            <i
                title={flagHelp}
                onClick={!permissions.one2ones.canFlag ? null : () => updateFlagFunc(c.id, !c.flagged)}
                className={`${isMe || !permissions.one2ones.canFlag ? '' : 'pointer'} mr10 fa fa-flag ${
                    c.flagged ? 'text-danger' : 'text-grey'
                }`}
            />
        );

        const help =
            c.description || ArrayHelpers.existsAndHasLength(c.positiveIndicators) ? (
                <Help id={`help-${c.id}`}>
                    <div className="mr10 ml10">
                        <h4>{c.full_Name}</h4>
                        {c.description && <Copy value={c.description} />}
                    </div>
                    <PositiveIndicators positiveIndicators={c.positiveIndicators} />
                </Help>
            ) : null;

        const special = (
            <span className="ml10 mr10" style={{ position: 'relative', top: '-5px' }}>
                <Rater
                    benchmark={c.percentile}
                    level={c.level}
                    onChange={permissions.one2ones.canRate ? level => updateRatingFunc(c.id, level) : null}
                />
            </span>
        );

        const buttons = (
            <>
                {(hasModule(user, 'objectives') || hasModule(user, 'blogs') || hasModule(user, 'knowledge')) && (
                    <button
                        onClick={() => {
                            posthog.capture('capability-search', { capability: c.full_Name });
                            props.history.push(`/search?competency=${c.id}&user=${props.profile?.id}`);
                        }}
                        className="btn-badge btn btn-xs btn-info hidden-xs"
                        title={text.buttons.search}
                        data-testid="button-e4-data-row-search"
                    >
                        <i className="fa fa-search"></i>
                    </button>
                )}
                <span className="btn-badge">
                    <button
                        className="btn btn-xs btn-info hidden-xs"
                        onClick={() => {
                            setShowOverTime(true);
                            setOverTimeCompetency(c);
                        }}
                        disabled={!c.level && !integrationsList.length}
                        title={text.buttons.overtime}
                        data-testid="button-e4-data-row-overtime"
                    >
                        <i className="fa fa-line-chart"></i>
                    </button>
                    {integrationsList.length > 0 && hasModule(user, 'integrations') && (
                        <span title={withText} className="hidden-xs">
                            <img className="crm-icon" src="/dist/img/metrics-icon-ric.png" alt="" />
                        </span>
                    )}
                </span>
                {hasModule(user, 'objectives') && permissions.one2ones.canAddObjectives && (
                    <button
                        onClick={() => addTask(c.id)}
                        className="btn btn-xs btn-info hidden-xs"
                        title={text.buttons.addObjective}
                        data-testid="button-e4-data-row-addobjective"
                    >
                        <i className="fa fa-plus-circle"></i>
                    </button>
                )}
                {hasModule(user, 'objectives') && permissions.one2ones.canViewObjectives && (
                    <span className="btn-badge">
                        <button
                            onClick={() => showTasksModal(c.id)}
                            className="btn btn-xs btn-info hidden-xs"
                            title={text.buttons.viewObjectives}
                            data-testid="button-e4-data-row-objectives"
                            disabled={tasks === 0}
                        >
                            <i className="fa fa-tasks"></i>
                        </button>
                        {pastTasks > 0 && <span className="badge label label-danger hidden-xs">{pastTasks}</span>}
                    </span>
                )}
            </>
        );

        return (
            <DataRow
                key={`competency-row-${c.id}-${i}`}
                prefix={prefix}
                title={c.name}
                help={help}
                special={special}
                buttons={buttons}
                testPrefix="competency"
            />
        );
    };

    const areaNavigator = () => (
        <div className="flex flex-center flex-grow">
            {!filterOnFocused && !filtered && (
                <>
                    <button
                        className="btn btn-xs"
                        data-testid="button-competencies-prev"
                        disabled={areas.length < 2}
                        onClick={() => setArea(areas[prevIndex])}
                    >
                        <i className="fa fa-arrow-left" />
                    </button>
                    <span className="mr10 ml10">{currentArea ? currentArea.name : text.none}</span>
                    {currentArea && (
                        <span className="mr10">
                            <label
                                title={text.averageRating}
                                style={{ position: 'relative', top: '-1px' }}
                                className={`label ${labelClass(filteredAverage)}`}
                            >
                                {filteredAverage > 0 ? filteredAverage : 'n / a'}
                            </label>
                        </span>
                    )}
                    <button
                        className="btn btn-xs"
                        data-testid="button-competencies-next"
                        disabled={areas.length < 2}
                        onClick={() => setArea(areas[nextIndex])}
                    >
                        <i className="fa fa-arrow-right" />
                    </button>
                </>
            )}
            {filtered ? (
                <button
                    className="btn btn-link ml10 text-sm"
                    data-testid="button-competencies-clear"
                    onClick={() => {
                        props.clearFilter();
                    }}
                >
                    {text.clear}
                </button>
            ) : (
                <button
                    className={`btn ml10 text-sm btn-primary`}
                    data-testid="button-competencies-flagged"
                    title={filterOnFocused ? text.byArea : text.focused}
                    onClick={() => setFilterOnFocused(!filterOnFocused)}
                >
                    {filterOnFocused ? text.byArea : text.focused}
                </button>
            )}
        </div>
    );

    return (
        <Box
            data-testid="user121-competencies-box"
            title={filtered ? text.filteredCompetencies : filterOnFocused ? text.focusAreas : text.title}
            help={translateSys(user.language_Code, systemTranslationTypes.ratings, user.companySettings, defaultRatingText)}
            button={areaNavigator}
        >
            {!loaded ? (
                <Loading />
            ) : (
                <div>
                    {currentArea
                        ? filteredCompetencies.map((c, i) => competencyCard(c, i))
                        : isMe
                        ? text.noCompetencies.you
                        : text.noCompetencies.them}
                </div>
            )}
            {showAddTask && (
                <ManageTask
                    values={{ profile_Ids: [props.profile.id] }}
                    show={showAddTask}
                    defaultCompetency={competency}
                    reload={() => api.loadCompetencies()}
                    close={() => setShowAddTask(false)}
                />
            )}
            {showTasks && (
                <CompetencyTasks
                    summary
                    profile={props.profile}
                    competency={competencies.find(c => c.id === competencyId)}
                    show={showTasks}
                    close={() => setShowTasks(false)}
                />
            )}
            {showOverTime && (
                <CompetencyOverTime
                    profile={props.profile}
                    competency={overTimeCompetency}
                    show={showOverTime}
                    close={() => {
                        setShowOverTime(false);
                        setOverTimeCompetency(null);
                    }}
                />
            )}
        </Box>
    );
};

export default withRouter(Competencies);
